import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { submitLeadForm } from "@services/smsLeads";
import { sanitizeFormValues } from "@utils/sanitize";

export default function LeadForm() {
  const [searchParams] = useSearchParams();
  const phone = searchParams.get("phone") || "";

  const [fullName, setFullName] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const missing = [];
    if (!phone) missing.push("Phone number");
    if (!fullName.trim()) missing.push("Full name");
    if (!clinicName.trim()) missing.push("Clinic name");
    if (!email.trim()) missing.push("Email");
    if (missing.length) {
      setError(`Please fill in: ${missing.join(", ")}`);
      return;
    }

    setSubmitting(true);
    try {
      await submitLeadForm(sanitizeFormValues({
        phone_number: phone,
        full_name: fullName.trim(),
        clinic_name: clinicName.trim(),
        email: email.trim(),
      }));
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full overflow-hidden">
          <div className="bg-indigo-600 px-8 py-8 text-white">
            <h1 className="text-2xl font-bold">Klinifai</h1>
            <p className="text-indigo-200 text-sm mt-1">AI-powered phone answering for medical clinics</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-indigo-600 px-8 py-8 text-white">
          <h1 className="text-2xl font-bold">Klinifai</h1>
          <p className="text-indigo-200 text-sm mt-1">AI-powered phone answering for medical clinics</p>
        </div>

        {/* Form */}
        <div className="px-8 py-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-5">Tell us about your clinic</h2>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-5">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Phone (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Phone Number
              </label>
              <input
                type="tel"
                value={phone}
                readOnly
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm bg-gray-50 text-gray-500 cursor-default"
              />
              <p className="text-xs text-gray-400 mt-1">This is the number you called us from.</p>
            </div>

            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Smith"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                autoComplete="name"
              />
            </div>

            {/* Clinic Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clinic Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={clinicName}
                onChange={(e) => setClinicName(e.target.value)}
                placeholder="Sunrise Family Dental"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourclinic.com"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-indigo-600 text-white font-semibold py-3 rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 disabled:cursor-not-allowed mt-2"
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </form>

          <p className="text-xs text-gray-400 text-center mt-4">
            By submitting this form you agree to be contacted by the Klinifai team.
          </p>
        </div>
      </div>
    </div>
  );
}
